import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  cn(
"pressable inline-flex shrink-0 cursor-pointer items-center justify-center whitespace-nowrap",
"rounded-tile border border-transparent font-rad font-semibold uppercase tracking-[.12em] scrim",
"",
"disabled:cursor-not-allowed disabled:opacity-40",
  ),
  {
    variants: {
      variant: {
        default: "border-accent/45 bg-accent/10 text-accent hover:border-accent/70 hover:bg-accent/20",
        solid: "border-accent bg-accent text-fg hover:border-accent-deep hover:bg-accent-deep",
        muted: "border-edge bg-track text-dim hover:border-edge-strong hover:bg-tile hover:text-fg",
        link: "text-accent underline-offset-4 tracking-normal normal-case hover:underline",

        destructive:
"border-danger/45 bg-danger/10 text-danger-soft hover:border-danger/70 hover:bg-danger/20",
        warning:
"border-amber/45 bg-amber/10 text-amber hover:border-amber/70 hover:bg-amber/20",
        jail:
"border-jail/45 bg-jail/10 text-jail hover:border-jail/70 hover:bg-jail/20",
        success:
"border-success/45 bg-success/10 text-success hover:border-success/70 hover:bg-success/20",
        info:
"border-thirst/45 bg-thirst/10 text-thirst-soft hover:border-thirst/70 hover:bg-thirst/20",
        secondary:
"border-edge bg-tile text-fg hover:border-edge-strong hover:bg-tile",

        ghost:
"border-transparent bg-transparent text-dim hover:bg-tile hover:text-fg",
"ghost-destructive":
"border-transparent bg-transparent text-danger-soft hover:border-danger/45 hover:bg-danger/10",
"ghost-warning":
"border-transparent bg-transparent text-amber hover:border-amber/45 hover:bg-amber/10",
"ghost-success":
"border-transparent bg-transparent text-success hover:border-success/45 hover:bg-success/10",
"ghost-info":
"border-transparent bg-transparent text-thirst-soft hover:border-thirst/45 hover:bg-thirst/10",
"ghost-secondary":
"border-transparent bg-transparent text-fg hover:bg-tile",
"ghost-muted":
"border-transparent bg-transparent text-faint hover:bg-tile hover:text-dim",

        outline:
"border-edge bg-tile text-fg hover:border-edge-strong hover:bg-tile",
"outline-destructive":
"border-danger/45 text-danger-soft hover:bg-danger/10 hover:border-danger/70",
"outline-warning":
"border-amber/45 text-amber hover:bg-amber/10 hover:border-amber/70",
"outline-jail":
"border-jail/45 text-jail hover:bg-jail/10 hover:border-jail/70",
"outline-success":
"border-success/45 text-success hover:bg-success/10 hover:border-success/70",
"outline-info":
"border-thirst/45 text-thirst-soft hover:bg-thirst/10 hover:border-thirst/70",
"outline-secondary":
"border-edge-strong text-fg hover:bg-tile",
"outline-muted":
"border-edge text-faint hover:bg-tile hover:text-dim",
      },
      size: {
        default: "min-h-11 gap-1.5 px-3 pt-[3px] text-caption lg:min-h-0 lg:h-9 [&_svg]:-mt-[3px]",
        sm: "min-h-11 gap-1.5 px-2.5 pt-0.5 text-label lg:min-h-0 lg:h-8 [&_svg]:-mt-0.5",
        lg: "min-h-12 gap-2.5 px-5 pt-0.5 text-base [&_svg]:-mt-0.5",
        xs: "h-7 gap-1 rounded-bar px-2 pt-0.5 text-label [&_svg]:-mt-0.5",
        inline: "h-5 gap-1 rounded-bar px-1.5 pt-px text-micro tracking-[.1em] [&_svg]:-mt-px",
        icon: "size-11 lg:size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
